import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import { UserContext } from "../App";
import useInvoke from "../Tools/Invoker";
import LoginRedirect from "../Page/LoginRedirect";
import CheckToken from "../_Login/CheckToken";
import toast from "react-hot-toast";
import GetDesignFolderDetails from "../DesignPrint/DesignPrintGetDesignFolderDetails";

import "react-tooltip/dist/react-tooltip.css";
import { Tooltip } from "react-tooltip";

import Draggable from "react-draggable";
import Xarrow, { useXarrow, Xwrapper } from "react-xarrows";

const SchematicDraw = () => {
  const { id } = useParams();
  const [user, setUser] = useContext(UserContext);
  let navigate = useNavigate();
  CheckToken(user["id"], user["token"]).then((result) => {
    if (!result) {
      localStorage.clear();
      setUser({});
      navigate("/Login");
    }
  });

  let response26 = useInvoke("WebApps", "26", { "@DesignId": id });

  const [configuration, setConfiguration] = useState([]);
  const [designFolderID, setDesignFolderID] = useState("");
  const [designFolderDetails, setDesignFolderDetails] = useState([]);
  const [rootNode, setRootNode] = useState(null);
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [layoutReady, setLayoutReady] = useState(false);
  const [showing, setShowing] = useState(true);

  const gridUnit = 12.5;
  const grid = [gridUnit, gridUnit];

  // Dimensions for each component box
  const height = 75;
  const width = 75;

  // Reduced spacing for a more compact layout
  const verticalSpacing = 120;
  const horizontalSpacing = 80;

  const style_title = {
    fontSize: "14px",
    fontWeight: "bold",
    width: "75px",
    textAlign: "center",
    marginTop: "-5px",
  };

  const style_name = {
    fontSize: "10px",
    fontWeight: "bold",
    width: "75px",
    textAlign: "center",
    position: "relative",
    background: "rgba(255,255,255,0.25)",
    color: "black",
    textShadow: "0.5px 0.5px lightgrey",
  };

  const style_innerContainer = {
    height: `${height}px`,
    width: `${width}px`,
  };

  const updateXarrow = useXarrow();

  // ─── DRAG HANDLERS ──────────────────────────────────────────────
  const handleStart = (e) => {
    document.querySelectorAll(".innerContainer").forEach((element) => {
      element.style.border = "1px dashed lightgrey";
    });
  };

  const handleStop = (e) => {
    updateXarrow();
    document.querySelectorAll(".innerContainer").forEach((element) => {
      element.style.border = "";
    });
  };

  const handleTooltipToggle = (e) => {
    setShowing(!showing);
  };

  // ─── PROCESS COMPONENTS TO BYPASS "N" NODES ─────────────────────
  const processComponents = (components) => {
    const comps = components.map((c) => ({ ...c, Children: [] }));
    comps.forEach((c) => {
      // If the parent is an "N" type, bypass it by reassigning to its parent.
      while (c.Parent !== "") {
        const parent = comps.find((p) => p.Type === c.Parent);
        if (parent && parent.Type.includes("N")) {
          c.Parent = parent.Parent;
        } else {
          break;
        }
      }
    });
    return comps.filter((c) => !c.Type.includes("N"));
  };

  // ─── BUILD THE TREE ─────────────────────────────────────────────
  // Using the convention that a node’s Parent matches its parent's Type.
  const buildTree = (components) => {
    let root = null;
    components.forEach((c) => {
      c.Children = [];
    });
    components.forEach((c) => {
      if (c.Parent !== "") {
        const parent = components.find((p) => p.Type === c.Parent);
        if (parent) {
          parent.Children.push(c);
        }
      } else {
        root = c;
      }
    });
    return root;
  };

  // ─── COMPUTE A SIMPLE TREE LAYOUT ──────────────────────────────
  // Assigns each leaf an increasing X value and centers parents over their children.
  const computeLayout = (node, depth = 0, pos = { counter: 0 }) => {
    node.Y = depth * verticalSpacing;
    if (!node.Children || node.Children.length === 0) {
      node.X = pos.counter * horizontalSpacing;
      pos.counter++;
    } else {
      node.Children.forEach((child) => computeLayout(child, depth + 1, pos));
      node.X =
        (node.Children[0].X + node.Children[node.Children.length - 1].X) / 2;
    }
  };

  const getTreeBounds = (node, bounds = { maxX: 0, maxY: 0 }) => {
    if (node.X > bounds.maxX) bounds.maxX = node.X;
    if (node.Y > bounds.maxY) bounds.maxY = node.Y;
    if (node.Children) {
      node.Children.forEach((child) => getTreeBounds(child, bounds));
    }
    return bounds;
  };

  // ─── RENDER A NODE (AND ITS CHILDREN) ───────────────────────────
  const RenderNode = ({ node }) => {
    if (!node.display) return null;
    return (
      <>
        <Draggable
          handle=".handle"
          defaultPosition={{ x: node.X, y: node.Y }}
          grid={grid}
          scale={1}
          onStart={handleStart}
          onDrag={updateXarrow}
          onStop={handleStop}
        >
          <div
            className="innerContainer"
            id={`comp_${node.Type}`}
            style={{
              ...style_innerContainer,
              padding: "0px 12.5px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "absolute",
            }}
            data-tooltip-id="my-tooltip"
            data-tooltip-html={node.datatooltip}
          >
            <div className="handle" style={style_title}>
              {node.Type}
            </div>
            <img
              className="handle"
              onMouseOver={() => { }}
              onMouseLeave={() => { }}
              height={height - 25}
              width={width - 25}
              draggable="false"
              src={node.ImageURL}
              alt={node.Component}
            />
            <div className="handle" style={style_name}>
              {node.Component}
            </div>
          </div>
        </Draggable>
        {node.Children &&
          node.Children.map((child) => (
            <React.Fragment key={child.Type}>
              <Xarrow
                zIndex={-1}
                start={`comp_${node.Type}`}
                end={`comp_${child.Type}`}
                startAnchor="bottom"
                endAnchor="top"
                color="lightgrey"
                strokeWidth={2}
                showTail={false}
                showHead={false}
                curveness={0}
                dashness={false}
              />
              <RenderNode node={child} />
            </React.Fragment>
          ))}
      </>
    );
  };

  // ─── PROCESS THE RESPONSE AND BUILD THE TREE ─────────────────────
  useEffect(() => {
    if (response26.data.payload !== undefined) {
      let data = JSON.parse(response26.data.payload).data[0];

      data["DesignNum"] = data[0];
      data["CompletedDate"] = data[1];
      data["InducedAirFlow"] = data[2];
      data["InjectedAirFlow"] = data[3];
      data["Total Discharge Air Flow"] = data[4];
      data["NegativeStaticPressure"] = data[5];
      data["BestNegativeStaticPressure"] = data[6];
      data["TotalStaticPressure"] = data[7];
      data["BestStaticPressure"] = data[8];
      data["SystemBalance"] = data[9];
      data["Notes"] = data[10] || data["Notes"];

      data["Components"] = [];

      if (data[11]) {
        let components = data[11].split("|");

        components.forEach((c, index) => {
          c = c.split("‡");

          // Only display if Type does not include "N"
          let display = c[2].includes("N") ? false : true;

          let num = c[1].toString();
          while (num.length < 2) num = `0${num}`;

          const tooltipParts = [];
          if (c[5]) tooltipParts.push(`Diameter: ${c[5]}"`);
          if (c[6]) tooltipParts.push(`Length: ${c[6]}`);
          if (c[7]) tooltipParts.push(`Velocity: ${c[7]}FPM`);
          if (c[8]) tooltipParts.push(`Loss: ${c[8]}`);
          if (c[10]) tooltipParts.push(`Note: ${c[10].replace("&amp;", "&")}`);

          const tooltip = tooltipParts.join("<br />");

          data["Components"].push({
            display: display,
            key: index,
            id: c[0],
            Item: num,
            Type: c[2],
            Category: c[3],
            Component: c[4].replace("&amp;", "&"),
            Diameter: `${c[5]}`,
            Length: `${c[6]}`,
            Velocity: `${c[7]}`,
            PressureLoss: c[8],
            CFM: c[9],
            Note: c[10].replace("&amp;", "&"),
            ImageURL: "https://dc.cssi.co/Images/" + c[11],
            Parent: c[12],
            datatooltip: tooltip,
          });
        });

        const processed = processComponents(data["Components"]);
        const treeRoot = buildTree(processed);
        setRootNode(treeRoot);
      }

      let num = data["12"].toString();
      while (num.length < 6) num = "0" + num;
      let sequence = data["DesignNum"];
      while (sequence.length < 2) sequence = "0" + sequence;
      data["DesignFolderNum"] = `DES#${num}-${sequence}`;
      setDesignFolderID(data["13"]);
      data.splice(0, 13);
      setConfiguration(data);
    }
  }, [response26.data.payload]);

  // ─── GET DESIGN FOLDER DETAILS ────────────────────────────────────
  useEffect(() => {
    if (designFolderID !== "") {
      GetDesignFolderDetails(designFolderID).then((response) => {
        response
          ? setDesignFolderDetails(JSON.parse(response.payload).data[0])
          : toast.error("An error has occurred.");
      });
    }
  }, [configuration]);

  // ─── ONCE WE HAVE A ROOT NODE, COMPUTE AND FLIP THE LAYOUT ───────
  useEffect(() => {
    if (rootNode) {
      computeLayout(rootNode, 0, { counter: 0 });
      // Get the maximum Y from the computed layout.
      const bounds = getTreeBounds(rootNode, { maxX: 0, maxY: 0 });
      // Flip the Y coordinates so that the root moves to the bottom.
      const flipLayout = (node, maxY) => {
        node.Y = maxY - node.Y;
        if (node.Children) {
          node.Children.forEach((child) => flipLayout(child, maxY));
        }
      };
      flipLayout(rootNode, bounds.maxY);
      // Recalculate bounds after flipping.
      const flippedBounds = getTreeBounds(rootNode, { maxX: 0, maxY: 0 });
      setContainerDimensions({
        width: flippedBounds.maxX + width + 50,
        height: flippedBounds.maxY + height + 50,
      });
      setLayoutReady(true);
    }
  }, [rootNode]);

  document.title = `Design - Schematic`;
  return (
    <>
      <Xwrapper>
        <div className="Schematic" style={{ display: "flex" }}>
          {designFolderDetails && (
            <div className="body-content" style={{ marginTop: "25px", width: "1100px", margin: "auto" }}>
              <div className="no-print" style={{ marginBottom: "px" }}>
                <Link to={`/Design/Edit/${id}`} className="btn btn-default" style={{ width: "132px" }}>
                  Back
                </Link>
                <button onClick={() => window.print()} className="btn btn-primary" style={{ width: "132px", marginLeft: "8px" }}>
                  Print This Page
                </button>
                <div
                  className="form-check form-switch"
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    marginTop: "-32px",
                    marginRight: "10px",
                  }}
                >
                  <input
                    onChange={handleTooltipToggle}
                    className="form-check-input"
                    type="checkbox"
                    id="tooltiptoggle"
                    defaultChecked={true}
                    style={{ marginTop: "-4px" }}
                  />
                  <label className="form-check-label" htmlFor="flexSwitchCheckChecked">
                    &nbsp;Show Hover Data
                  </label>
                </div>
              </div>
              {/* Info header */}
              <table width="100%">
                <tbody>
                  <tr>
                    <td rowSpan="2" style={{ width: "30%" }}>
                      <img src="data:image/png;base64,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" height="30px;" style={{ paddingRight: "25px" }} />
                    </td>
                    <td style={{ width: "70%", textAlign: "right", fontWeight: "bold" }}>
                      Sales Application Design for {configuration["DesignFolderNum"]} (Compass Ref #
                      {designFolderDetails[3]})
                    </td>
                  </tr>
                  <tr>
                    <td style={{ textAlign: "right" }}>
                      Created {designFolderDetails[1]} by {designFolderDetails[2]} &nbsp;&nbsp;&nbsp;
                      {configuration["CompletedDate"] == null ? (
                        <>Status: In Process</>
                      ) : (
                        <>Status: <b>Finalized</b></>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>

              <hr
                style={{
                  color: "black",
                  padding: "0px",
                  border: "2px solid black",
                  marginTop: "5px",
                  marginBottom: "10px",
                  background: "transparent",
                  display: "block",
                  width: "100%",
                }}
              />

              <table width="100%">
                <tbody>
                  <tr>
                    <td style={{ width: "33%", fontWeight: "bold" }}>Customer</td>
                    <td style={{ width: "33%", fontWeight: "bold" }}>Site Location</td>
                    <td rowSpan="2" style={{ width: "33%", verticalAlign: "top" }}>
                      <b>Industry:</b> {designFolderDetails[15]}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ fontWeight: "bold" }}>{designFolderDetails[5]}</td>
                    <td style={{ fontWeight: "bold" }}>{designFolderDetails[8]}</td>
                  </tr>
                  <tr>
                    <td>{designFolderDetails[6]}</td>
                    <td>{designFolderDetails[9]}</td>
                    <td rowSpan="2" style={{ verticalAlign: "top" }}>
                      <b>Materials Handled:</b> {designFolderDetails[16]}
                    </td>
                  </tr>
                  <tr>
                    <td>{designFolderDetails[7]}</td>
                    <td>{designFolderDetails[10]}</td>
                  </tr>
                  <tr>
                    <td>
                      <b>Customer Ref#:</b> {designFolderDetails[4]}
                    </td>
                    <td>
                      <b>Elevation:</b> {designFolderDetails[11]} ft
                    </td>
                    <td>{designFolderDetails[17]}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Render the computed tree */}
        <div
          className="Schematic_Components"
          style={{
            position: "relative",
            width: containerDimensions.width,
            height: containerDimensions.height,
            margin: "32px auto"
          }}
        >
          {layoutReady && rootNode && <RenderNode node={rootNode} />}
          {showing && (
            <Tooltip
              id="my-tooltip"
              multiline={true}
              style={{ backgroundColor: "rgba(0, 0, 0, 0.9)", color: "white" }}
            />
          )}
        </div>

      </Xwrapper>
    </>
  );
};

export default SchematicDraw;
